import { useState } from 'react'
import {
  IconButton,
  Popover,
  Typography,
  Badge,
  Divider,
  Grid,
  MenuItem,
  Tooltip,
  Stack,
  Button,
  ListSubheader
} from '@mui/material'

import { format } from '@utils/date-fns_functions'

import {
  Close as CloseIcon,
  FilterList,
  RestartAlt as RestartAltIcon
} from '@mui/icons-material'
import DatePicker from '@components/form/DatePicker'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

import { useForm } from 'react-hook-form'

import { useTranslation, TranslationContextType } from '@contexts/translation'

import useStyles from '@components/common/Filter/styles'
import { useAuth } from '@contexts/auth'
import { useFetchData } from '@api/fetchData'
import API_URL from '@config/services'
import Select from '../SelectUncontrolled'
import {
  costStatus,
  isRecuperation,
  paymentType,
  validStatus,
  isImported,
  dateFilter,
  projectStatus,
  invoiceOpenStatus,
  invoiceDateFilter
} from './filters_types'
import queryString from '@utils/queryString'
import { useRouter } from 'next/router'

// Define the type for the filter options/lists
interface FilterOption {
  value: string | number
  label: string
  handleClick: () => void
}

// Define the prop types for GeneralFilter
interface GeneralFilterProps {
  disabled?: boolean
  filterActivated?: number
  queries: Record<string, any>
  setQueries: (queries: Record<string, any>) => void
  initialQueries?: Record<string, any>
  clearButton?: boolean
  project?: boolean
  orguser?: boolean
  contractor?: boolean
  is_paid?: boolean
  payment_type?: boolean
  timelog_type?: boolean
  valid_status?: boolean
  is_recuperation?: boolean
  cost_status?: boolean
  role?: boolean
  tag?: boolean
  project_tags?: boolean
  sync_with_budget?: boolean
  sync_with_planning?: boolean
  phase?: boolean
  annex?: boolean
  cities?: boolean
  currencies?: boolean
  is_open?: boolean
  type?: boolean
  isPagination?: boolean
  is_internal?: boolean
  is_imported?: boolean
  user_state?: boolean
  user_status?: boolean
  tags?: boolean
  projectId?: number | null
  client_currency?: boolean
  payment_type_different?: boolean
  date?: boolean
  dateRequestedOn?: boolean
  stage?: boolean
  project_status?: boolean
  billable?: boolean
  reimbursable?: boolean
  expenseCategories?: boolean
  expenseStatus?: boolean
  job_status?: boolean
  invoice_open_status?: boolean
  cities_projects?: boolean | null
  cities_opportunities?: boolean | null
  suppliers?: boolean
  SetFilterQuery?: (query: Record<string, any>) => void
  invoice?: boolean
  lease?: boolean
  building?: boolean
  floor?: boolean
  active?: boolean
  mortgageType?: boolean
  finance?: boolean
  priority?: boolean
  status?: boolean
  occupancy?: boolean
  accounts?: boolean
  accountsList?: any[]
  tenant?: boolean
  landlord?: boolean
  task_list?: boolean
  tasks_status?: boolean
  tasks_states?: boolean
  currentTab?: string
  height?: string | null
}

// Type for form values
interface FormValues {
  start_date: Date | null
  end_date: Date | null
}

const GeneralFilter: React.FC<GeneralFilterProps> = ({
  disabled = false,
  filterActivated = 0,
  queries,
  setQueries,
  initialQueries = {},
  clearButton = true,
  project = false,
  orguser = false,
  contractor = false,
  is_paid = false,
  payment_type = false,
  timelog_type = false,
  valid_status = false,
  is_recuperation = false,
  cost_status = false,
  role = false,
  tag = false,
  project_tags = false,
  sync_with_budget = false,
  sync_with_planning = false,
  phase = false,
  annex = false,
  cities = false,
  currencies = false,
  is_open = false,
  type = false,
  // clients = false,
  isPagination = false,
  is_internal = false,
  is_imported = false,
  user_state = false,
  user_status = false,
  tags = false,
  projectId = null,
  client_currency = false,
  payment_type_different = false,
  date = false,
  dateRequestedOn = false,
  stage,
  project_status,
  billable,
  reimbursable,
  expenseCategories,
  expenseStatus,
  job_status,
  invoice_open_status,
  cities_projects = null,
  cities_opportunities = null,
  suppliers = false,
  //years = [],
  SetFilterQuery = () => {},
  invoice = false,
  lease = false,
  building = false,
  floor = false,
  active = false,
  mortgageType = false,
  finance = false,
  priority = false,
  status = false,
  occupancy = false,
  accounts = false,
  accountsList = [],
  tenant = false,
  landlord = false,
  task_list = false,
  tasks_status = false,
  tasks_states = false,
  currentTab,
  height = null
}) => {
  const { organization, organizationTeam } = useAuth()
  const [openDatePicker, setOpenDatePicker] = useState<boolean>(false)
  const router = useRouter()

  const classes = useStyles()
  const { dictionary, t } = useTranslation() as TranslationContextType & {
    dictionary: Record<string, any>
  }

  // Schema for the date form
  const schema = yup.object().shape({
    start_date: yup.date().nullable().required(dictionary.required),
    end_date: yup
      .date()
      .nullable()
      .required(dictionary.required)
      .min(yup.ref('start_date'), dictionary.end_date_after_start)
  })

  const {
    watch,
    formState: { errors },
    control,
    handleSubmit
  } = useForm<FormValues>({
    mode: 'onBlur',
    resolver: yupResolver(schema),
    defaultValues: {
      start_date: null,
      end_date: null
    }
  })

  // Type list definition
  const TYPE_LIST: FilterOption[] = [
    { label: dictionary.permanent, value: 'permanent', handleClick: () => {} },
    {
      label: dictionary.permanent_mission,
      value: 'permanent_mission',
      handleClick: () => {}
    },
    {
      label: dictionary.permanent_construction,
      value: 'permanent_construction',
      handleClick: () => {}
    },
    {
      label: dictionary.fixed_term,
      value: 'fixed_term',
      handleClick: () => {}
    },
    { label: dictionary.interim, value: 'temporary', handleClick: () => {} },
    {
      label: dictionary.apprenticeship,
      value: 'apprenticeship',
      handleClick: () => {}
    },
    {
      label: dictionary.professionalization,
      value: 'professionalization',
      handleClick: () => {}
    },
    { label: dictionary.freelancer, value: 'freelance', handleClick: () => {} },
    {
      label: dictionary.service_delivery,
      value: 'service delivery',
      handleClick: () => {}
    },
    { label: dictionary.intern, value: 'intern', handleClick: () => {} },
    { label: dictionary.bonus, value: 'bonus', handleClick: () => {} }
  ]

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [dateValue, setDateValue] = useState<string>('')

  /* fetch data that needs to be fetched */
  const { data: _projectsList } = useFetchData<any>(
    project ? API_URL.PROJECTS.GET_PROJECT_LIST_PK : null
  )

  const { data: _orgusersList } = useFetchData<any>(
    orguser
      ? API_URL.ORG_USER.GET_ORG_USER_PK(organization) +
          '?' +
          queryString({
            team: organizationTeam,
            page_size: 999,
            all_users: true
          })
      : null
  )

  // Add more API calls and data processing as needed

  // Event handlers
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'filter-popover' : undefined

  return (
    <>
      <IconButton
        color="warning"
        disabled={disabled}
        onClick={handleClick}
        sx={{ marginRight: 3 }}
        data-testid="IconButton-filter"
      >
        <Badge badgeContent={filterActivated} color="warning">
          <FilterList />
        </Badge>
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        className={classes.root}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left'
        }}
      >
        <Typography variant="h6" p={2}>
          Filter Options (Implementation in progress)
        </Typography>
      </Popover>
    </>
  )
}

export default GeneralFilter
